import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import mergeClassNames from '../../../lib/style-helpers/merge-class-name';
import ProgressBar from '../../progress-bar/progress-bar';
import fileDisplayInfo from '../../../models/fileDisplayInfo';
import { FileToken } from '../../../types/FileToken';
import { UIProps } from '../../../types/StyledProps';
import localizeDate from '../../../lib/localization/localize-date';
import { FontSize, FontType } from '../../../../../config/theme/types/themeTypes';

export type FileCardProps = React.PropsWithoutRef<UIProps & FileToken>;

function FileCardLower({
  className,
  delay = 0,
  name,
  type,
  dateUploaded,
  progress = 0,
  ...props
}: FileCardProps) {
  const delayProp = {
    delay: (delay) / 3,
  };

  const { shortLabel, imagePath } = fileDisplayInfo(type);

  return (
    <Container
      className={mergeClassNames('FileCard', className)}
      {...props}
    >
      <Card {...delayProp}>
        <RingContainer>
          <LargeRing />
          <MediumRing />
          <SmallRing />
        </RingContainer>
        <IconWrapper>
          <ImageIcon
            src={imagePath}
            alt={'icon'}
          />
        </IconWrapper>
        <TypeLabel>
          {shortLabel}
        </TypeLabel>
        <Content>
          <ProgressBar progress={progress} />
        </Content>
      </Card>
      <Title>
        {name}
      </Title>
      <DateStamp>
        {localizeDate(new Date(dateUploaded), 'MMMM D, YYYY h:mm A')}
      </DateStamp>
    </Container>
  );
}

const Container = styled.div`
  padding: 0px 16px;
  width: 236px;
  height:194px;
  transform: translateY(0px);
  transition: transform 0.2s;
  &:hover {
    transform: translateY(-5px);

  }
  ${({ theme }) => theme.media.tablet} {
    width: 100%;
    max-width: 100%;
    margin-left: 0px;
    margin-right: 0px; 
    padding: 0px;
    &:hover {
      transform: translateY(0px);
    }
  }
`;

const Card = styled.div <UIProps>`
  height:90px;
  z-index: 1;
  display: flex;
  flex-direction: row;
  opacity: 0;
  overflow: hidden;
  position: relative;
  padding: 24px 24px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 2px 10px rgb(0 0 0 / 20%);
  animation: pop-in 0.5s ${({ delay }) => delay}s forwards;
  @keyframes pop-in {
    0% {
      opacity: 0;
      transform: scale(0.5);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const Content = styled.div`
  z-index: 1;
  display: flex;
  flex: 1 1 80%;
  flex-direction: column;
  text-align: left;
  padding-left: 12px;
  overflow: hidden;
`;

const IconWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
`;

const TypeLabel = styled.div`
  position: absolute;
  top: 65;
  left: 18;
  margin-top: 0;
  overflow: hidden;
  white-space: nowrap;
  ${({ theme }) => theme.font(FontType.SemiBold, FontSize.Small)};
  color: ${({ theme }) => theme.colors.black};
`;

const Title = styled.div`
  margin-top: 10;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  letter-spacing: -0.10px !important;
  margin-bottom: 4px;
  line-height: 15px;
  font-size:15px;
  ${({ theme }) => theme.font(FontType.SemiBold, FontSize.Medium)};
  color: ${({ theme }) => theme.colors.black};
`;

const DateStamp = styled.div`
  padding-top: 1px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 15px;
  ${({ theme }) => theme.font(FontType.Regular, FontSize.Small)};
  color: ${({ theme }) => theme.palette.grey400};
`;

const ImageIcon = styled.img`
  position:absolute;
  top: 15;
  left: 15;   
  width: 30px;
  height: 30px;
`;

const RingContainer = styled.div`
  position: absolute;
  top: -24px;
  right: 0px;
  transform: translate(50%, -50%);
  width: 230px;
  height: 230px;
  ${({ theme }) => theme.media.tablet} {
    right: 115px;
  }
`;

const LargeRing = styled.div`
  width: 230px;
  height: 230px;
  border-radius: 50%;
  border: 1px solid rgb(242, 242, 242);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  }
`;

const MediumRing = styled.div`
  width: 170px;
  height: 170px;
  border-radius: 50%;
  border: 1px solid rgb(242, 242, 242);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  }
`;

const SmallRing = styled.div`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  border: 1px solid rgb(242, 242, 242);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  }
`;

export default observer(FileCardLower);
